/**
 * 通知関連フック
 *
 * SPEC 7.1.1 通知ドロワー準拠
 * BE: GET/PUT /admin/notifications
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFetch, useMutation } from './useApi';
import { apiFetch } from '@/api/client';
import { useToast } from '@/hooks/useToast';
import { parseContentDispositionFilename } from '@/hooks/useExportCreditorsCsv';
import type { Notification } from '@/types';

/**
 * 楽観的 unread override の TTL (ms)。
 *
 * 既読 mutation 直後、未読数 API (`getUnreadCount`) は UNREAD-INDEX (GSI) を参照する。
 * GSI は結果整合のため mark 直後の再取得が一時的に古い高い値を返し得る
 * (RO 調査 2026-06-18: 1 秒差の read-all が同一集合を再処理する lag を実機観測)。
 * この窓の間だけサーバ値を無視して楽観値を表示し、窓を過ぎたら再取得して
 * サーバ値 (新着を含む実数) を正とする。
 */
const OPTIMISTIC_UNREAD_TTL_MS = 5000;

// ==============================
// pure ロジック (FL: 既読後の未読数即時更新 / ドロワー未読のみ表示)
// React 副作用から切り離し、useNotifications.test.ts で網羅する。
// ==============================

/** 未読通知のみ抽出する (ドロワーは未読のみ表示する) */
export function selectUnreadNotifications(items: Notification[]): Notification[] {
  return items.filter((n) => !n.isRead);
}

/** 個別既読時の楽観的な未読数 (下限 0) */
export function nextUnreadCountAfterReadOne(current: number): number {
  return Math.max(0, current - 1);
}

/**
 * optimistic に既読化した notifId のうち「まだサーバが未読として返している」ものだけ
 * 残す。サーバ反映が追いついた (既読 or 一覧から消えた) id は落とす。
 *
 * これにより、既読 mutation 直後 〜 list 再取得が反映されるまでの間も対象通知を
 * ドロワーから隠し続けつつ (= 即時に消える)、サーバが既読を返したら optimistic 集合を
 * 自然に縮小させて二重管理を避ける (再表示によるチラつきを防ぐ)。
 */
export function reconcileOptimisticReadIds(
  optimisticIds: string[],
  items: Notification[],
): string[] {
  const next = optimisticIds.filter((id) => items.some((n) => n.notifId === id && !n.isRead));
  // 除去が 1 件も起きていないなら引数の配列をそのまま返す。
  // `filter` は内容が同じでも必ず新しい配列を返すため、これを setState に渡すと
  // 「内容は不変なのに identity だけ変わる」state 更新になり React が bail out できない。
  // 呼び出し元 (NotificationDrawer) はこれを notifications 依存の effect から呼ぶため、
  // 再 render → effect 再発火 → setState の無限ループになる (全ページ loading 停止の原因)。
  return next.length === optimisticIds.length ? optimisticIds : next;
}

/** 通知一覧のページ (BE: GET /admin/notifications) */
interface NotificationPage {
  items: Notification[];
  /** 次ページがあるときだけ返る opaque cursor */
  nextCursor?: string;
}

/** ドロワーが 1 ページに要求する件数 (BE 既定と揃える。BE 上限は 100) */
export const NOTIFICATION_PAGE_SIZE = 50;

/**
 * loading 中・data 不在時に使う共有の空配列。
 *
 * `data?.items ?? []` のように render のたびに新しい `[]` を作ると、そこから派生する
 * `notifications` の identity も毎 render 変わり、それを dependency に持つ consumer の
 * effect が再発火し続ける。空である限り中身は同じなので 1 つを使い回す。
 * **この配列は破壊的変更しないこと** (派生処理は必ず新しい配列を返す)。
 */
const EMPTY_NOTIFICATIONS: Notification[] = [];

/**
 * 通知一覧 URL を組み立てる。
 *
 * S2: ドロワーは `unreadOnly=true` でサーバ側に未読を絞らせる。
 * 以前はクエリ無しで呼んで「最新 50 件 (既読込み)」を引き、そこから
 * クライアント側で未読を抜いていたため、最新 50 件から外れた未読は
 * 到達不能だった。
 */
export function buildNotificationsUrl(opts: {
  unreadOnly?: boolean;
  limit?: number;
  cursor?: string;
}): string {
  const q = new URLSearchParams();
  if (opts.unreadOnly) q.set('unreadOnly', 'true');
  if (opts.limit) q.set('limit', String(opts.limit));
  if (opts.cursor) q.set('cursor', opts.cursor);
  const qs = q.toString();
  return qs ? `/admin/notifications?${qs}` : '/admin/notifications';
}

/**
 * 取得済みリストへ次ページを追加する。
 *
 * notifId で重複を落とし、既存の並び (新しい順) を崩さない。
 * ページ境界で同じ通知が再送されても二重に積まない。
 */
export function appendNotificationPage(
  existing: Notification[],
  incoming: Notification[],
): Notification[] {
  const seen = new Set(existing.map((n) => n.notifId));
  const added: Notification[] = [];
  for (const n of incoming) {
    if (seen.has(n.notifId)) continue;
    seen.add(n.notifId);
    added.push(n);
  }
  return [...existing, ...added];
}

/**
 * 通知一覧取得。
 *
 * 引数なしの呼び出しは従来どおり「全通知の最新ページ」を返す (既存利用箇所の挙動を変えない)。
 * `unreadOnly` を指定すると未読専用経路になり、`nextCursor` があるあいだ
 * `loadMore()` で古い未読を追加取得できる。
 */
export function useNotifications(
  options: { unreadOnly?: boolean; limit?: number; enabled?: boolean } = {},
): {
  notifications: Notification[];
  error: string | null;
  loading: boolean;
  refetch: () => void;
  /** 次ページがある場合のみ true (= 「さらに読み込む」を出す条件) */
  hasMore: boolean;
  /** 追加取得中 (二重要求の抑止に使う) */
  loadingMore: boolean;
  loadMore: () => void;
} {
  const { unreadOnly = false, limit, enabled = true } = options;
  const firstPageUrl = buildNotificationsUrl({ unreadOnly, limit });
  const { data, error, loading, refetch } = useFetch<NotificationPage>(
    enabled ? firstPageUrl : null,
  );

  // 追加取得したページ (先頭ページは useFetch 側が持つ)。
  const [extraItems, setExtraItems] = useState<Notification[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [loadingMore, setLoadingMore] = useState(false);
  // 追加取得の多重発火を防ぐ。state だと同一 tick 内の連打を止められない。
  const inFlightRef = useRef(false);

  // 先頭ページが取り直されたら追加分を捨てて cursor を引き直す
  // (再取得後に古いページが混ざったままにならないようにする)。
  useEffect(() => {
    setExtraItems([]);
    setCursor(data?.nextCursor);
  }, [data]);

  const firstPageItems = data?.items ?? EMPTY_NOTIFICATIONS;
  // 派生配列を毎 render 作り直すと identity が変わるため、`notifications` を dependency に
  // 持つ consumer (NotificationDrawer の optimistic 既読 reconcile) の effect が永久に
  // 再発火し、再 render を無限に誘発する。実際に元データの identity が変わったときだけ
  // 作り直す (先頭ページと追加ページのどちらが変わっても再計算される)。
  const notifications = useMemo(
    () => appendNotificationPage(firstPageItems, extraItems),
    [firstPageItems, extraItems],
  );

  const loadMore = useCallback(() => {
    if (!cursor || inFlightRef.current) return;
    inFlightRef.current = true;
    setLoadingMore(true);
    void (async () => {
      try {
        const page = await apiFetch<NotificationPage>(
          buildNotificationsUrl({ unreadOnly, limit, cursor }),
        );
        // 追加のみ行うため、失敗しても既取得分は消えない。
        setExtraItems((prev) => appendNotificationPage(prev, page.items ?? []));
        setCursor(page.nextCursor);
      } catch {
        // 表示済みは維持し、cursor を据え置いて再試行できるようにする。
      } finally {
        inFlightRef.current = false;
        setLoadingMore(false);
      }
    })();
  }, [cursor, unreadOnly, limit]);

  return {
    notifications,
    error,
    loading,
    refetch,
    hasMore: !!cursor,
    loadingMore,
    loadMore,
  };
}

/** 未読数の楽観更新アクション */
export type OptimisticUnreadAction = 'reset' | 'decrement' | 'clear';

/** 未読数取得 — QA-133: 30秒ごとに自動再取得 (通知遅延対策) */
export function useUnreadCount() {
  const { data, loading, refetch } = useFetch<{ unreadCount: number; count?: number }>(
    '/admin/notifications/unread-count',
  );
  const serverCount = data?.unreadCount ?? data?.count ?? 0;

  // FL: 既読 mutation 直後にバッジを即時反映する楽観的 override。
  // null = override なし (サーバ値をそのまま表示)。{ value, until } は TTL 付き。
  const [override, setOverride] = useState<{ value: number; until: number } | null>(null);

  // 最新 serverCount を ref に保持 (override 計算の stale closure 回避)
  const serverCountRef = useRef(serverCount);
  serverCountRef.current = serverCount;

  // override の TTL 失効時に override を解除し、未読数を再取得して
  // サーバ値 (GSI 反映後) に整合させる。
  useEffect(() => {
    if (!override) return;
    const ms = override.until - Date.now();
    if (ms <= 0) {
      setOverride(null);
      refetch();
      return;
    }
    const id = setTimeout(() => {
      setOverride(null);
      refetch();
    }, ms);
    return () => clearTimeout(id);
  }, [override, refetch]);

  // ポーリング: 30 秒ごとに未読数を再取得
  useEffect(() => {
    const id = setInterval(() => {
      refetch();
    }, 30000);
    return () => clearInterval(id);
  }, [refetch]);

  /**
   * 未読数を楽観的に更新する。
   * - 'reset'     → 0 (全て既読)
   * - 'decrement' → max(0, n-1) (個別既読)
   * - 'clear'     → override 解除 (mutation 失敗時のロールバック: サーバ値に戻す)
   */
  const applyOptimisticUnread = useCallback((action: OptimisticUnreadAction) => {
    if (action === 'clear') {
      setOverride(null);
      return;
    }
    setOverride((prev) => {
      const active = prev && prev.until > Date.now();
      const base = active ? prev.value : serverCountRef.current;
      const value = action === 'reset' ? 0 : nextUnreadCountAfterReadOne(base);
      return { value, until: Date.now() + OPTIMISTIC_UNREAD_TTL_MS };
    });
  }, []);

  const active = override && override.until > Date.now() ? override.value : null;

  return {
    unreadCount: active ?? serverCount,
    loading,
    refetch,
    applyOptimisticUnread,
  };
}

/** 個別既読マーク */
export function useMarkAsRead(onSuccess?: () => void) {
  const { mutate, loading } = useMutation(async (notifId: string) => {
    const res = await apiFetch<{ notifId: string; isRead: boolean }>(
      `/admin/notifications/${notifId}/read`,
      { method: 'PUT' },
    );
    onSuccess?.();
    return res;
  });

  return { markAsRead: mutate, loading };
}

/** 一括既読マーク */
/** 一括既読 1 リクエストの応答 (BE: PUT /admin/notifications/read-all) */
interface MarkAllChunkResult {
  /** 当該リクエストで既読化した件数 */
  processedCount?: number;
  /** 同じ snapshot 内にまだ処理対象が残っているか */
  hasMore?: boolean;
  /** 次リクエストへそのまま渡す固定 snapshot token */
  snapshotToken?: string;
  /** 旧 BE のみが返す総件数 (後方互換) */
  updatedCount?: number;
}

/** 1 回の「すべて既読」操作で許容するリクエスト数の上限 (無限ループ防止) */
export const MARK_ALL_MAX_REQUESTS = 100;

/** 一括既読の進捗 */
export interface MarkAllProgress {
  /** 累計で既読化した件数 */
  processedCount: number;
  /** 上限に達してまだ残っている (失敗ではない) */
  reachedRequestLimit: boolean;
}

/** 一括既読 URL を組み立てる (2 回目以降は同じ snapshot token を渡す) */
export function buildMarkAllUrl(snapshotToken?: string): string {
  return snapshotToken
    ? `/admin/notifications/read-all?snapshotToken=${encodeURIComponent(snapshotToken)}`
    : '/admin/notifications/read-all';
}

/**
 * 一括既読。
 *
 * S3: BE は 1 リクエストあたり有限件数しか処理しないため、`hasMore` の間だけ
 * 同じ snapshot token で**逐次**呼び直す (並列に投げない)。
 *
 * - 進まない応答 (処理 0 件なのに続きあり) を 1 度でも受けたら止める
 * - 上限リクエスト数に達したら「失敗」ではなく「上限到達」として終了する
 * - 旧 BE (`hasMore` を返さない) に対しては 1 回で完了とみなす
 */
export function useMarkAllAsRead(onSuccess?: () => void) {
  const markAllFn = useCallback(async (): Promise<MarkAllProgress> => {
    let processedCount = 0;
    let snapshotToken: string | undefined;
    let reachedRequestLimit = false;

    for (let i = 0; i < MARK_ALL_MAX_REQUESTS; i += 1) {
      const res = await apiFetch<MarkAllChunkResult>(buildMarkAllUrl(snapshotToken), {
        method: 'PUT',
      });

      // 旧 BE は processedCount を返さず updatedCount だけを返す
      const done = res.processedCount ?? res.updatedCount ?? 0;
      processedCount += done;

      // 旧 BE は hasMore を返さないので「続きなし」と解釈する (エラーにしない)
      if (!res.hasMore || !res.snapshotToken) break;
      // 続きがあるのに 1 件も進まないなら、繰り返しても前進しないため止める
      if (done === 0) break;

      snapshotToken = res.snapshotToken;
      if (i === MARK_ALL_MAX_REQUESTS - 1) reachedRequestLimit = true;
    }

    onSuccess?.();
    return { processedCount, reachedRequestLimit };
  }, [onSuccess]);

  const { mutate, loading } = useMutation(async (_: void) => {
    return markAllFn();
  });

  const markAllAsRead = useCallback(() => mutate(undefined as void), [mutate]);

  return { markAllAsRead, loading };
}

// ==============================
// 対応履歴 (通知履歴) CSV export (steering 321)
// ==============================

/**
 * 対応履歴 (通知履歴) CSV export の request URL を構築する純関数
 * (Network / Amplify から分離してテスト可能にする)。
 * 例: buildExportHistoryUrl('/api') → '/api/admin/notifications/history/export'
 */
export function buildExportHistoryUrl(apiBase: string): string {
  return `${apiBase}/admin/notifications/history/export`;
}

/**
 * 対応履歴 CSV export フック (steering 321)。
 *
 * BE の既存エンドポイント GET /admin/notifications/history/export
 * (user-scoped: PK=USER#{userId} / 監査 notification.history_exported 済 / Content-Disposition)
 * を叩いて CSV (text/csv) を取得し、Blob download する。
 * apiFetch は JSON レスポンス前提のため、CSV は fetch + Blob で直接扱う
 * (useExportCreditorsCsv と同型: 認証トークンは fetchAuthSession から取得し
 *  Authorization ヘッダーで付与する。URL に token/secret は載せない)。
 */
export function useExportHistory() {
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();

  const exportHistory = useCallback(async (): Promise<boolean> => {
    setLoading(true);
    try {
      // apiFetch と同じ認証トークン取得ロジック (session JWT を Authorization で付与)
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      const authToken = session.tokens?.idToken?.toString() ?? '';

      const apiBase = import.meta.env.DEV
        ? '/api'
        : import.meta.env.VITE_API_URL || 'https://api.ace-collect.com';

      const res = await fetch(buildExportHistoryUrl(apiBase), {
        headers: {
          ...(authToken && { Authorization: `Bearer ${authToken}` }),
        },
      });

      // 失敗を成功と誤認しない (CLAUDE.md §4.5: response.ok を必ず確認する)
      if (!res.ok) {
        throw new Error(`CSV 出力に失敗しました (${res.status})`);
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Content-Disposition から filename を取得、なければ BE の命名規則に揃えた fallback
      a.download = parseContentDispositionFilename(
        res.headers.get('Content-Disposition'),
        'notification-history.csv',
      );
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      showToast('success', 'CSV をダウンロードしました');
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'CSV 出力に失敗しました';
      showToast('error', message);
      return false;
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  return { exportHistory, loading };
}
